import AdminCrudList from "./AdminCrudList";
import { Link } from "react-router-dom";
import { useState } from "react";
import { RefreshCw, Film } from "lucide-react";
import { api, formatErr } from "@/lib/api";

export function AdminServices() {
  return (
    <AdminCrudList
      title="Servicios"
      endpoint="services"
      idLabel="title"
      defaults={{ active: true, featured: false, order: 0, icon: "Sparkles", category: "General" }}
      fields={[
        { key: "title", label: "Título", type: "text" },
        { key: "category", label: "Categoría", type: "text" },
        { key: "icon", label: "Icono (lucide)", type: "text" },
        { key: "short_description", label: "Descripción corta", type: "text" },
        { key: "long_description", label: "Descripción larga", type: "textarea" },
        { key: "image", label: "Imagen", type: "image" },
        { key: "cta_label", label: "CTA", type: "text" },
        { key: "order", label: "Orden", type: "number" },
        { key: "active", label: "Activo", type: "boolean" },
        { key: "featured", label: "Destacado", type: "boolean" },
      ]}
    />
  );
}

export function AdminTalents() {
  return (
    <AdminCrudList
      title="Talentos"
      endpoint="talents"
      idLabel="artist_name"
      defaults={{ active: true, featured: false, order: 0, country: "Bolivia", category: "Lifestyle" }}
      formExtras={({ editing, setEditing }) => editing?.id ? <SyncBlock editing={editing} setEditing={setEditing} /> : <p className="text-xs text-muted-foreground">Guardá el talento primero para habilitar el sync con redes sociales.</p>}
      rowExtras={(t) => (
        <Link to={`/admin/talents/${t.id}/works`} className="inline-flex items-center gap-1 px-3 py-1.5 border border-border rounded-sm text-xs font-semibold mr-2 hover:border-primary" data-testid={`works-link-${t.id}`}>
          <Film className="w-3 h-3" /> Trabajos
        </Link>
      )}
      fields={[
        { key: "artist_name", label: "Nombre artístico", type: "text" },
        { key: "full_name", label: "Nombre completo", type: "text" },
        { key: "city", label: "Ciudad", type: "text" },
        { key: "country", label: "País", type: "text" },
        { key: "category", label: "Categoría", type: "text" },
        { key: "photo", label: "Foto principal", type: "image" },
        { key: "photo_secondary", label: "Foto secundaria", type: "image" },
        { key: "short_description", label: "Descripción corta", type: "text" },
        { key: "bio", label: "Bio", type: "textarea" },
        { key: "followers_instagram", label: "Followers IG", type: "number" },
        { key: "followers_tiktok", label: "Followers TikTok", type: "number" },
        { key: "followers_youtube", label: "Followers YouTube", type: "number" },
        { key: "followers_facebook", label: "Followers Facebook", type: "number" },
        { key: "engagement", label: "Engagement %", type: "number" },
        { key: "reach", label: "Alcance", type: "number" },
        { key: "instagram", label: "Instagram URL", type: "text" },
        { key: "tiktok", label: "TikTok URL", type: "text" },
        { key: "youtube", label: "YouTube URL", type: "text" },
        { key: "facebook", label: "Facebook URL", type: "text" },
        { key: "twitch", label: "Twitch URL", type: "text" },
        { key: "x", label: "X URL", type: "text" },
        { key: "linkedin", label: "LinkedIn URL", type: "text" },
        { key: "web", label: "Web", type: "text" },
        { key: "order", label: "Orden", type: "number" },
        { key: "active", label: "Activo", type: "boolean" },
        { key: "featured", label: "Destacado", type: "boolean" },
      ]}
    />
  );
}

export function AdminProducts() {
  return (
    <AdminCrudList
      title="Productos"
      endpoint="products"
      idLabel="name"
      defaults={{ active: true, featured: false, order: 0, status: "idea", category: "General" }}
      fields={[
        { key: "name", label: "Nombre", type: "text" },
        { key: "category", label: "Categoría", type: "text" },
        { key: "image", label: "Imagen", type: "image" },
        { key: "short_description", label: "Descripción corta", type: "text" },
        { key: "long_description", label: "Descripción larga", type: "textarea" },
        { key: "talent_name", label: "Talento asociado", type: "text" },
        { key: "status", label: "Estado", type: "select", options: ["idea", "en_desarrollo", "lanzado"] },
        { key: "link", label: "Link", type: "text" },
        { key: "cta_label", label: "CTA", type: "text" },
        { key: "order", label: "Orden", type: "number" },
        { key: "active", label: "Activo", type: "boolean" },
        { key: "featured", label: "Destacado", type: "boolean" },
      ]}
    />
  );
}

export function AdminClients() {
  return (
    <AdminCrudList
      title="Clientes / Logos"
      endpoint="clients"
      idLabel="name"
      defaults={{ active: true, show_in_carousel: true, order: 0 }}
      fields={[
        { key: "name", label: "Nombre", type: "text" },
        { key: "logo", label: "Logo", type: "image" },
        { key: "link", label: "Link", type: "text" },
        { key: "order", label: "Orden", type: "number" },
        { key: "active", label: "Activo", type: "boolean" },
        { key: "show_in_carousel", label: "Mostrar en carrusel", type: "boolean" },
      ]}
    />
  );
}

export function AdminAbout() {
  return (
    <AdminCrudList
      title="Quiénes Somos"
      endpoint="about-blocks"
      idLabel="title"
      defaults={{ active: true, order: 0 }}
      fields={[
        { key: "title", label: "Título", type: "text" },
        { key: "description", label: "Descripción", type: "textarea" },
        { key: "image", label: "Imagen", type: "image" },
        { key: "order", label: "Orden", type: "number" },
        { key: "active", label: "Activo", type: "boolean" },
      ]}
    />
  );
}

function SyncBlock({ editing, setEditing }) {
  const [igHandle, setIgHandle] = useState((editing.instagram || "").replace(/^https?:\/\/(www\.)?instagram\.com\//, "").replace(/\/$/, ""));
  const [ttHandle, setTtHandle] = useState((editing.tiktok || "").replace(/^https?:\/\/(www\.)?tiktok\.com\/@?/, "").replace(/\/$/, ""));
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState({ type: "", text: "" });

  const sync = async (platform, handle) => {
    if (!handle) return setMsg({ type: "error", text: "Ingresá el username." });
    setBusy(platform); setMsg({ type: "", text: "" });
    try {
      const data = await api.post(`/admin/talents/${editing.id}/sync/${platform}`, { username: handle, apply: false });
      const update = { ...editing };
      if (platform === "instagram") {
        update.instagram = `https://instagram.com/${data.username}`;
        update.followers_instagram = data.followers || update.followers_instagram;
        if (data.profile_pic_url) update.photo = data.profile_pic_url;
        if (data.full_name && !update.full_name) update.full_name = data.full_name;
        if (data.biography && !update.bio) update.bio = data.biography;
      } else {
        update.tiktok = `https://tiktok.com/@${data.username}`;
        update.followers_tiktok = data.followers || update.followers_tiktok;
        if (data.profile_pic_url && !update.photo) update.photo = data.profile_pic_url;
      }
      setEditing(update);
      setMsg({ type: "ok", text: `${platform === "instagram" ? "Instagram" : "TikTok"}: ${data.full_name || data.username} · ${(data.followers || 0).toLocaleString()} seguidores. Click "Guardar" para aplicar.` });
    } catch (e) {
      setMsg({ type: "error", text: formatErr(e.response?.data?.detail) || "Error al sincronizar" });
    } finally {
      setBusy("");
    }
  };

  return (
    <div data-testid="sync-block">
      <h4 className="font-display font-bold text-sm uppercase tracking-widest mb-3">Sincronizar redes sociales</h4>
      <div className="grid md:grid-cols-2 gap-3">
        <div className="flex gap-2">
          <input value={igHandle} onChange={(e) => setIgHandle(e.target.value)} placeholder="@usuario IG" className="flex-1 h-10 px-3 bg-background border border-border rounded-sm text-sm focus:border-primary outline-none" />
          <button type="button" onClick={() => sync("instagram", igHandle)} disabled={busy === "instagram"} data-testid="sync-ig" className="px-4 py-2 border border-border rounded-sm text-xs font-semibold hover:border-primary disabled:opacity-50 inline-flex items-center gap-1">
            <RefreshCw className={`w-3 h-3 ${busy === "instagram" ? "animate-spin" : ""}`} /> {busy === "instagram" ? "..." : "Sync IG"}
          </button>
        </div>
        <div className="flex gap-2">
          <input value={ttHandle} onChange={(e) => setTtHandle(e.target.value)} placeholder="@usuario TikTok" className="flex-1 h-10 px-3 bg-background border border-border rounded-sm text-sm focus:border-primary outline-none" />
          <button type="button" onClick={() => sync("tiktok", ttHandle)} disabled={busy === "tiktok"} data-testid="sync-tt" className="px-4 py-2 border border-border rounded-sm text-xs font-semibold hover:border-primary disabled:opacity-50 inline-flex items-center gap-1">
            <RefreshCw className={`w-3 h-3 ${busy === "tiktok" ? "animate-spin" : ""}`} /> {busy === "tiktok" ? "..." : "Sync TT"}
          </button>
        </div>
      </div>
      {msg.text && <p className={`text-xs mt-2 ${msg.type === "ok" ? "text-primary" : "text-destructive"}`}>{msg.text}</p>}
    </div>
  );
}

